import { Injectable } from '@angular/core';
import { SessionService } from '@com-delta-idp/mnx-oauth';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { SKY_CLUB_ERROR } from '../../models';
import {
  AddToCart,
  AddToCartError,
  CartRequest,
  PurchaseResponse
} from '../../models/api/sky-club-api.interfaces';
import { PurchaseService } from '../../services/api/purchase.service';
import { validateUser } from '../../utils/user-session.utils';
import * as APIactions from '../actions';

@Injectable()
export class PurchaseEffects {
  loadPurchaseContent$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(APIactions.loadPurchaseContent),
        switchMap(() =>
          this.session.userData$.pipe(
            filter((userSession) => validateUser(userSession)),
            switchMap(() => {
              return this.purchase.getPurchaseOptions().pipe(
                map((response: PurchaseResponse) => {
                  return response.code
                    ? APIactions.loadPurchaseContentFail({
                        error: {
                          id: 'purchase-data-error',
                          purchaseDataError: response.code
                        }
                      })
                    : APIactions.loadPurchaseContentSuccess({
                        data: {
                          id: 'purchase-data',
                          purchaseData: response
                        }
                      });
                }),
                catchError(() =>
                  of(
                    APIactions.loadPurchaseContentFail({
                      error: {
                        id: 'purchase-data-error',
                        purchaseDataError: SKY_CLUB_ERROR
                      }
                    })
                  )
                )
              );
            })
          )
        )
      )
  );

  loadAddToCart$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(APIactions.loadAddToCart),
        switchMap((request: { data: CartRequest }) => {
          return this.purchase.addToCart(request.data.addToCart).pipe(
            map((response: AddToCart) => {
              return APIactions.loadAddToCartSuccess({
                data: {
                  id: 'add-to-cart-data',
                  addToCart: response,
                  value: request.data.value
                }
              });
            }),
            catchError((error: AddToCartError) => {
              const code = error?.error?.errorCode
                ? error?.error?.errorCode
                : SKY_CLUB_ERROR;
              return of(
                APIactions.loadAddToCartFail({
                  error: {
                    id: 'add-to-cart-error',
                    addToCartError: code
                  }
                })
              );
            })
          );
        })
      )
  );

  constructor(
    private actions$: Actions,
    private purchase: PurchaseService,
    private session: SessionService
  ) {}
}
